<template>
  <section class="lines sec" id="lines">
    <div class="page">
      <SectionHeading
        eyebrow="Core Business"
        title="四条业务线，一套技术底座"
        desc="从田间地头到生产车间，再到企业管理系统，皓辰用同一套物联网与数据能力服务不同产业现场。"
      />

      <div class="lines-grid">
        <article
          v-for="(line, i) in lines"
          :key="line.key"
          class="line-card reveal"
          v-reveal="{ delay: i * 120 }"
          :style="{ '--accent': line.accent, '--accent2': line.accent2 }"
        >
          <router-link :to="line.path" class="line-link">
            <div class="line-cover">
              <img :src="img(line.cover)" :alt="line.name"/>
              <span class="line-shade"></span>
              <span class="line-en">{{ line.en }}</span>
            </div>

            <div class="line-body">
              <h3 class="line-name">
                {{ line.name }}
                <IconSvg name="arrow-right"/>
              </h3>
              <p class="line-tagline">{{ line.tagline }}</p>
              <p class="line-summary">{{ line.summary }}</p>

              <ul class="line-points">
                <li v-for="(c, ci) in line.capabilities.slice(0, 3)" :key="ci">
                  <span class="point-dot"></span>{{ c.title }}
                </li>
              </ul>
            </div>
          </router-link>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHeading from '@/components/common/SectionHeading'
import IconSvg from '@/components/common/IconSvg'
import img from '@/utils/assets'
import { businessLines } from '@/data/site'

export default {
  name: 'BusinessLinesSection',
  components: { SectionHeading, IconSvg },
  data() {
    return { lines: businessLines, img }
  }
}
</script>

<style lang="scss" scoped>
.lines { position: relative; }

.lines-grid {
  display: grid;
  /* 业务线数量变化时整行铺满，避免最后一张卡片单独占一行留白 */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}

.line-card {
  position: relative;
  border-radius: 20px;
  border: 1px solid var(--line);
  background: linear-gradient(165deg, rgba(13, 27, 54, 0.9), rgba(6, 13, 29, 0.9));
  overflow: hidden;
  transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1), border-color 0.5s, box-shadow 0.5s;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(140deg, color-mix(in srgb, var(--accent) 55%, transparent), transparent 45%);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.5s;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-10px);
    border-color: transparent;
    box-shadow: 0 30px 70px -30px color-mix(in srgb, var(--accent) 70%, transparent);
  }

  &:hover::before { opacity: 1; }
  &:hover .line-cover img { transform: scale(1.07); }
  &:hover .line-name svg { transform: translateX(5px); }
}

.line-link { display: block; height: 100%; }

.line-cover {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.8s cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  .line-shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(3, 7, 17, 0.25) 0%, rgba(5, 12, 27, 0.72) 68%, rgba(6, 13, 29, 0.98) 100%);
  }

  .line-en {
    position: absolute;
    left: 22px;
    bottom: 16px;
    font-size: 11.5px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--accent-txt);
  }
}

.line-body { padding: 24px 26px 30px; }

.line-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  color: #fff;

  svg { width: 20px; height: 20px; color: var(--accent-txt); transition: transform 0.4s; }
}

.line-tagline {
  margin-top: 8px;
  font-size: 14px;
  color: var(--accent-txt);
  letter-spacing: 0.04em;
}

.line-summary {
  margin-top: 14px;
  font-size: 14px;
  line-height: 1.9;
  color: var(--txt-2);
  min-height: 76px;
}

.line-points {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(120, 176, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 13.5px;
    color: var(--txt-3);
  }

  .point-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 10px var(--accent);
    flex-shrink: 0;
  }
}

@media (max-width: 1180px) {
  .lines-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .line-summary { min-height: 0; }
}

@media (max-width: 760px) {
  .lines-grid { grid-template-columns: minmax(0, 1fr); gap: 18px; }
  .line-name { font-size: 20px; }
}
</style>
